import axios from "axios";
import { buildRequestConfig } from "./buildRequestConfig";
import {
    API_BASE_PATHS,
    API_ROUTES,
    ILibraryOutlet,
    ILibraryOutletCreateRequest,
    ILibraryOutletsResponse,
    ILibraryOutletUpdateRequest,
    IProfile,
    IProfilesResponse,
    IProfileCreateRequest,
    IProfileUpdateRequest,
    IVersion,
    IVersionsResponse,
    IVersionCreateRequest,
    IVersionUpdateRequest,
    IBlock,
    IBlocksResponse,
    IBlockCreateRequest,
    IBlockUpdateRequest,
} from "../libs";


class BlocksService {
    async getLibraryOutlets(): Promise<ILibraryOutlet[]> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get<ILibraryOutletsResponse>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.LIBRARY_OUTLETS}`,
                { ...config }
            );
            return response.data.data.outlets;
        } catch (err: any) {
            console.log("There is an error while fetching library outlets", err);
            throw new Error(err);
        }
    }

    async createLibraryOutlet(data: ILibraryOutletCreateRequest): Promise<ILibraryOutlet> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post<ILibraryOutlet>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.LIBRARY_OUTLETS}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while creating library outlet", err);
            throw new Error(err);
        }
    }
    async updateLibraryOutlet(data: ILibraryOutletUpdateRequest): Promise<ILibraryOutlet> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.patch<ILibraryOutlet>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.LIBRARY_OUTLETS}/${data.outletId}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while updating library outlet", err);
            throw new Error(err);
        }
    }

    // Profile methods
    async getProfiles(): Promise<IProfile[]> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get<IProfilesResponse>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}`,
                { ...config }
            );
            return response.data.data.profiles;
        } catch (err: any) {
            console.log("There is an error while fetching profiles", err);
            throw new Error(err);
        }
    }

    async createProfile(data: IProfileCreateRequest): Promise<IProfile> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post<IProfile>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILE}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while creating profile", err);
            throw new Error(err);
        }
    }

    async updateProfile(profileId: string, data: IProfileUpdateRequest): Promise<IProfile> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.patch<IProfile>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILE}/${profileId}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while updating profile", err);
            throw new Error(err);
        }
    }

    async invalidateProfileCache(clientId: string, profileId: string): Promise<{ invalidated: number }> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post<{ invalidated: number }>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/cache/invalidate/profile/${clientId}/${profileId}`,
                {},
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while invalidating profile cache", err);
            throw new Error(err);
        }
    }

    // Version methods
    async getVersions(profileId: string): Promise<IVersion[]> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get<IVersionsResponse>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}`,
                { ...config }
            );
            return response.data.data.versions;
        } catch (err: any) {
            console.log("There is an error while fetching versions", err);
            throw new Error(err);
        }
    }

    async createVersion(profileId: string, data: IVersionCreateRequest): Promise<IVersion> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post<IVersion>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while creating version", err);
            throw new Error(err);
        }
    }

    async updateVersion(profileId: string, versionId: string, data: IVersionUpdateRequest): Promise<IVersion> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.patch<IVersion>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while updating version", err);
            throw new Error(err);
        }
    }

    async activateVersion(profileId: string, versionId: string): Promise<void> {
        try {
            const config = await buildRequestConfig();
            await axios.patch(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}/${API_ROUTES.ACTIVATE_VERSION}`,
                {},
                { ...config }
            );
        } catch (err: any) {
            console.log("There is an error while activating version", err);
            throw new Error(err);
        }
    }

    // Block methods
    async getBlocks(profileId: string, versionId: string): Promise<IBlock[]> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get<IBlocksResponse>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}/${API_ROUTES.BLOCKS}`,
                { ...config }
            );
            return response.data.data.blocks;
        } catch (err: any) {
            console.log("There is an error while fetching blocks", err);
            throw new Error(err);
        }
    }

    async createBlock(profileId: string, versionId: string, data: IBlockCreateRequest): Promise<IBlock> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post<IBlock>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}/${API_ROUTES.BLOCKS}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while creating block", err);
            throw new Error(err);
        }
    }

    async updateBlock(profileId: string, versionId: string, blockId: string, data: IBlockUpdateRequest): Promise<IBlock> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.patch<IBlock>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}/${API_ROUTES.BLOCKS}/${blockId}`,
                data,
                { ...config }
            );
            return response.data;
        } catch (err: any) {
            console.log("There is an error while updating block", err);
            throw new Error(err);
        }
    }

    async deleteBlock(profileId: string, versionId: string, blockId: string): Promise<void> {
        try {
            const config = await buildRequestConfig();
            await axios.delete(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}/${API_ROUTES.BLOCKS}/${blockId}`,
                { ...config }
            );
        } catch (err: any) {
            console.log("There is an error while deleting block", err);
            throw new Error(err);
        }
    }

    async updateBlocksOrder(profileId: string, versionId: string, blocksOrder: string[]): Promise<void> {
        try {
            const config = await buildRequestConfig();
            await axios.patch(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.BLOCKS_API}/${API_ROUTES.PROFILES}/${profileId}/${API_ROUTES.VERSIONS}/${versionId}`,
                { blocksOrder },
                { ...config }
            );
        } catch (err: any) {
            console.log("There is an error while updating blocks order", err);
            throw new Error(err);
        }
    }
}
export const blocksService = new BlocksService();
